<template>
  <div class="textarea-main-container">
    <div>{{ props.label }}</div>
    <textarea
      :class="[
        'textarea-container',
        {
          small__: props.size == 'sm',
          medium__: props.size == 'md',
          large__: props.size == 'lg',
          'no-resize__': !props.resizable,
          extra__: props.maxCharacter && characterCount > props.maxCharacter,
        },
      ]"
      @input="textareaOnInput"
      v-model="text"
    ></textarea>
    <div
      v-if="props.maxCharacter"
      :class="[
        'character-count',
        {
          extra__: characterCount > props.maxCharacter,
        },
      ]"
    >
      {{ characterCount }} / {{ props.maxCharacter }} character{{
        characterCount > 1 ? "s" : ""
      }}
    </div>
    <div v-else class="character-count">
      {{ characterCount }} character{{ characterCount > 1 ? "s" : "" }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { withDefaults, defineProps, defineEmits, ref, onMounted } from "vue";
import { TextareaEmits, TextAreaProps } from "./componentModel/TextAreaModel";

// #region Data
/** The count of the character entered by the user in the textarea */
const characterCount = ref<number>(0);
/** The text that is binded to the textarea */
const text = ref<string>("");
// #endregion Data

const props = withDefaults(defineProps<TextAreaProps>(), {
  label: "Textarea",
  size: "md",
  resize: false,
  maxCharacter: 0,
  allowTypingAfterMax: false,
});

const emit = defineEmits<TextareaEmits>();

// #region Methods
const textareaOnInput = (e: Event) => {
  const textarea = e.target as HTMLTextAreaElement;
  const value = textarea.value;

  characterCount.value = value.length;

  if (props.maxCharacter > 0) {
    if (value.length >= props.maxCharacter && !props.allowTypingAfterMax) {
      text.value = text.value.substring(0, props.maxCharacter);
      characterCount.value = props.maxCharacter;
    }
  }

  emit("update:value", value);
};
// #endregion Methods

// #region Lifecycle
onMounted(() => {
  if (props.value) {
    text.value = props.value;
  }
});
// #endregion Lifecycle
</script>

<style scoped lang="scss">
.textarea-main-container {
  display: flex;
  flex-direction: column;
}
.textarea-container {
  width: 100%;
  padding: 5px 10px;
  background-color: rgba(250, 250, 250, 0.2);
  border: 2px solid rgba(180, 180, 180, 0.4);
  border-radius: 5px;

  &.large__ {
    height: 175px;
  }

  &.medium__ {
    height: 125px;
  }

  &.small__ {
    height: 75px;
  }

  &.no-resize__ {
    resize: none;
  }

  &.extra__ {
    border: 2px solid indianred !important;
  }

  &:focus {
    border: 2px solid gray;
    outline: none;
  }
}
.character-count {
  align-self: flex-end;

  &.extra__ {
    color: indianred;
  }
}
</style>
